import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { PlaidAccessService, usePlaidAccess } from '../../services/plaid-access-service';

/**
 * Settings → "Bank connections (Plaid)" (issue #42). Paid plans only: free
 * users never see this section. A household adds its OWN Plaid keys and can
 * then connect banks with them; FolioWorth's shared Plaid credentials stay
 * with platform operators.
 */
export const PlaidKeysSection: React.FC = () => {
  const { access, loaded, refresh } = usePlaidAccess();
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [env, setEnv] = useState<'production' | 'sandbox'>('production');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => { if (!loaded) void refresh(); }, [loaded, refresh]);

  if (!access?.can_add_keys) return null;
  const keys = access.byo;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await PlaidAccessService.save({ client_id: clientId, secret, environment: env, label: label || undefined });
      setSecret('');
      setEditing(false);
      setNotice('Plaid keys saved and verified. Bank connections are now available under Transactions → Connections.');
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save the keys');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove your Plaid keys? Connections made with them pause until keys are added again.')) return;
    await PlaidAccessService.remove();
    setNotice('Plaid keys removed.');
    await refresh();
  };

  const form = !keys || editing;
  return (
    <Card sx={{ maxWidth: 600, mb: 3 }} data-testid="plaid-keys-section">
      <CardHeader title="Bank connections (Plaid)" />
      <CardContent>
        <Stack spacing={2}>
          {access.paused && <Alert severity="warning">{access.pause_note}</Alert>}
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
          {!keys && (
            <Typography variant="body2" color="text.secondary">
              Add your Plaid keys to connect banks. Create a Plaid account at plaid.com, then paste the client ID and
              secret for the environment you were approved for. Keys are verified with Plaid and stored encrypted; the
              secret is never shown again.
            </Typography>
          )}
          {keys && !editing && (
            <Box>
              <Typography variant="body2"><b>{keys.label}</b> · {keys.environment}</Typography>
              <Typography variant="body2" className="fw-num">Client ID {keys.client_id} · secret ••••{keys.secret_last4}</Typography>
              {keys.validated_at && (
                <Typography variant="caption" color="text.secondary">Verified with Plaid {new Date(keys.validated_at).toLocaleDateString()}</Typography>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" onClick={() => { setEditing(true); setClientId(keys.client_id); setEnv(keys.environment); setLabel(keys.label); }}>
                  Replace keys
                </Button>
                <Button size="small" color="error" onClick={() => void remove()}>Remove</Button>
              </Stack>
            </Box>
          )}
          {form && (
            <>
              <TextField label="Plaid client ID" value={clientId} onChange={(e) => setClientId(e.target.value)} size="small"
                autoComplete="off" inputProps={{ 'aria-label': 'Plaid client ID' }} />
              <TextField label="Plaid secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} size="small"
                autoComplete="new-password" inputProps={{ 'aria-label': 'Plaid secret' }} />
              <Stack direction="row" spacing={2}>
                <TextField select label="Environment" value={env} size="small" sx={{ minWidth: 180 }}
                  onChange={(e) => setEnv(e.target.value as 'production' | 'sandbox')}>
                  <MenuItem value="production">Production</MenuItem>
                  <MenuItem value="sandbox">Sandbox</MenuItem>
                </TextField>
                <TextField label="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} size="small" fullWidth />
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={() => void save()} disabled={saving || !clientId.trim() || !secret.trim()}>
                  {saving ? 'Verifying…' : 'Save keys'}
                </Button>
                {editing && <Button onClick={() => setEditing(false)}>Cancel</Button>}
              </Stack>
            </>
          )}
          {access.webhook_url && (
            <Box>
              <Typography variant="caption" color="text.secondary" component="div">
                In your Plaid dashboard, set the webhook URL to:
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" className="fw-num" sx={{ wordBreak: 'break-all' }} data-testid="plaid-webhook-url">
                  {access.webhook_url}
                </Typography>
                <Tooltip title="Copy">
                  <IconButton size="small" aria-label="Copy webhook URL" onClick={() => void navigator.clipboard?.writeText(access.webhook_url!)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Plaid uses it to tell FolioWorth about new transactions and connections that need you to sign in again.
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
